/**
 * Catches a render-time crash anywhere below it, so the page shows a message instead of
 * going blank. Retry remounts the children.
 */

import { Component, Fragment, type ErrorInfo, type ReactNode } from 'react';
import { Alert, AlertTitle, Box, Button } from '@mui/material';

interface Props {
    /** Names what failed, e.g. "The summary report grid". */
    label?: string;
    children: ReactNode;
}

interface State {
    /** Bumped on retry, to remount the subtree rather than re-render the failed one. */
    attempt: number;
    failed: boolean;
}

export default class SummaryReportsBoundary extends Component<Props, State> {
    state: State = { attempt: 0, failed: false };

    static getDerivedStateFromError(): Partial<State> {
        return { failed: true };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        console.error(`SummaryReports: ${this.props.label ?? 'This view'} crashed`, error, info);
    }

    retry = () => {
        this.setState((s) => ({ attempt: s.attempt + 1, failed: false }));
    };

    render() {
        // Keyed fragment: retry remounts the subtree without adding a layout box.
        if (!this.state.failed)
            return <Fragment key={this.state.attempt}>{this.props.children}</Fragment>;

        return (
            <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
                <Alert
                    severity='error'
                    sx={{ maxWidth: 560 }}
                    action={
                        <Button color='inherit' size='small' onClick={this.retry}>
                            Retry
                        </Button>
                    }
                >
                    <AlertTitle>Something went wrong</AlertTitle>
                    {this.props.label ?? 'This view'} stopped unexpectedly. Retry to reload it, or
                    close the page and try again.
                </Alert>
            </Box>
        );
    }
}
